import React, { useState } from "react";

import {
  Alert,
  Button,
  Modal,
  Result,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import fileDownload from "js-file-download";
import PropTypes from "prop-types";

import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  InboxOutlined,
} from "@ant-design/icons";

import { createBulkPayouts, validateBulkPayouts } from "../../Pages/api";

const { Text, Title } = Typography;
const { Dragger } = Upload;

// CSV field names that match API expectations
const CSV_HEADERS = [
  "email",
  "phone",
  "host",
  "transactionType",
  "amount",
  "periodStartDate",
  "periodEndDate",
  "releaseDate",
  "remark",
];

// At least one of email or phone is required to identify the partner
// Note: releaseDate is intentionally NOT required here. It is required for
// debit entries and optional for credit entries; this per-row rule is
// enforced during validation (server-side) rather than at the column level.
const REQUIRED_HEADERS = [
  "host",
  "transactionType",
  "amount",
  "periodStartDate",
  "periodEndDate",
];

// Either email or phone must be present
const IDENTIFIER_HEADERS = ["email", "phone"];

const parseCsvData = (csvText) => {
  const lines = csvText.split("\n").filter((line) => line.trim());

  if (lines.length < 2) {
    throw new Error(
      "CSV file must contain at least a header row and one data row"
    );
  }

  // Parse headers
  const headers = lines[0].split(",").map((h) => h.trim());

  // Validate required headers
  const missingHeaders = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
  if (missingHeaders.length > 0) {
    throw new Error(`Missing required columns: ${missingHeaders.join(", ")}`);
  }

  // Check that at least one identifier column (email or phone) is present
  const hasIdentifier = IDENTIFIER_HEADERS.some((h) => headers.includes(h));
  if (!hasIdentifier) {
    throw new Error(
      `At least one identifier column is required: ${IDENTIFIER_HEADERS.join(
        " or "
      )}`
    );
  }

  const entries = [];

  for (let i = 1; i < lines.length; i += 1) {
    // Handle CSV values with potential commas in quoted strings
    const values = [];
    let currentValue = "";
    let insideQuotes = false;

    for (let j = 0; j < lines[i].length; j += 1) {
      const char = lines[i][j];
      if (char === '"') {
        insideQuotes = !insideQuotes;
      } else if (char === "," && !insideQuotes) {
        values.push(currentValue.trim());
        currentValue = "";
      } else {
        currentValue += char;
      }
    }
    values.push(currentValue.trim());

    const entry = {};
    headers.forEach((header, index) => {
      const value = values[index] || "";
      // Clean up any surrounding quotes
      const cleanedValue = value.replace(/^"|"$/g, "").trim();
      // Only add non-empty values to the entry
      if (cleanedValue !== "") {
        entry[header] = cleanedValue;
      }
    });

    // Convert amount to number if present
    if (entry.amount) {
      entry.amount = Number(entry.amount);
    }

    // Validate transactionType
    if (entry.transactionType) {
      entry.transactionType = entry.transactionType.toLowerCase();
    }

    entries.push(entry);
  }

  return entries;
};

const generateSampleCSV = () => {
  // Using DD-MM-YYYY format as per requirement
  const sampleData = [
    {
      email: "user1@example.com",
      phone: "9876543210",
      host: "tagmango.com",
      transactionType: "credit",
      amount: 1000,
      periodStartDate: "01-01-2025",
      periodEndDate: "31-01-2025",
      releaseDate: "",
      remark: "January payout",
    },
    {
      email: "user2@example.com",
      phone: "9123456789",
      host: "tagmango.com",
      transactionType: "debit",
      amount: 500,
      periodStartDate: "01-01-2025",
      periodEndDate: "31-01-2025",
      releaseDate: "01-02-2025",
      remark: "",
    },
  ];

  const header = CSV_HEADERS.join(",");
  const rows = sampleData.map((entry) =>
    CSV_HEADERS.map((h) => entry[h] || "").join(",")
  );

  return `${header}\n${rows.join("\n")}`;
};

const generateValidationResultCSV = (entries) => {
  const headers = [...CSV_HEADERS, "isValid", "errorMessage"];
  const header = headers.join(",");

  const rows = entries.map((entry) =>
    headers
      .map((h) => {
        const value = entry[h];
        if (value === undefined || value === null) return "";
        // Escape values with commas or quotes
        const strValue = String(value);
        if (strValue.includes(",") || strValue.includes('"')) {
          return `"${strValue.replace(/"/g, '""')}"`;
        }
        return strValue;
      })
      .join(",")
  );

  return `${header}\n${rows.join("\n")}`;
};

// Format date for display
const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

// Convert date to ISO format for API
// Input format: DD-MM-YYYY (primary), also supports YYYY-MM-DD and DD/MM/YYYY
// Output: ISO string with start of day or end of day based on isEndOfDay flag
const convertToISODate = (dateStr, isEndOfDay = false) => {
  if (!dateStr) return "";

  try {
    // Handle various date formats
    let date;

    // Check if already in ISO format
    if (dateStr.includes("T")) {
      date = new Date(dateStr);
    } else if (dateStr.includes("-")) {
      const parts = dateStr.split("-");
      if (parts[0].length === 4) {
        // YYYY-MM-DD format
        date = new Date(`${parts[0]}-${parts[1]}-${parts[2]}`);
      } else {
        // DD-MM-YYYY format (primary expected format)
        date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
    } else if (dateStr.includes("/")) {
      const parts = dateStr.split("/");
      // DD/MM/YYYY format
      date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    } else {
      date = new Date(dateStr);
    }

    if (Number.isNaN(date.getTime())) {
      return dateStr;
    }

    if (isEndOfDay) {
      date.setHours(23, 59, 59, 999);
    } else {
      date.setHours(0, 0, 0, 0);
    }

    return date.toISOString();
  } catch {
    return dateStr;
  }
};

const BulkPayoutModal = ({ visible, onClose, onSuccess }) => {
  const [step, setStep] = useState("upload"); // upload, validating, results, creating, success
  const [validatedEntries, setValidatedEntries] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const resetModal = () => {
    setStep("upload");
    setValidatedEntries([]);
    setError(null);
    setLoading(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleDownloadSample = () => {
    const csv = generateSampleCSV();
    fileDownload(csv, "bulk_payout_sample.csv");
  };

  const handleFileUpload = async (file) => {
    setError(null);
    setLoading(true);
    setStep("validating");

    try {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const csvText = e.target.result;
          const entries = parseCsvData(csvText);

          if (entries.length === 0) {
            throw new Error("No valid entries found in CSV");
          }

          // Convert dates to ISO format for API
          const apiEntries = entries.map((entry) => {
            const apiEntry = {
              ...entry,
              periodStartDate: convertToISODate(entry.periodStartDate),
              periodEndDate: convertToISODate(entry.periodEndDate, true),
            };

            // Only include releaseDate when one is provided (optional for
            // credit entries). Avoid sending an empty value to the backend.
            const releaseDate = convertToISODate(entry.releaseDate);
            if (releaseDate) {
              apiEntry.releaseDate = releaseDate;
            } else {
              delete apiEntry.releaseDate;
            }

            return apiEntry;
          });

          // Call validation API
          const response = await validateBulkPayouts({ entries: apiEntries });

          if (response?.code === 200 && response?.result?.entries) {
            setValidatedEntries(response.result.entries);
            setStep("results");
          } else {
            throw new Error(response?.message || "Validation failed");
          }
        } catch (err) {
          setError(err.message || "Error processing CSV file");
          setStep("upload");
        } finally {
          setLoading(false);
        }
      };

      reader.onerror = () => {
        setError("Error reading CSV file");
        setStep("upload");
        setLoading(false);
      };

      reader.readAsText(file);
    } catch (err) {
      setError(err.message || "Error uploading file");
      setStep("upload");
      setLoading(false);
    }

    return false; // Prevent default upload
  };

  const handleDownloadValidationResults = () => {
    const csv = generateValidationResultCSV(validatedEntries);
    fileDownload(csv, "bulk_payout_validation_results.csv");
  };

  const handleCreatePayouts = async () => {
    setLoading(true);
    setStep("creating");

    try {
      // Filter only valid entries and format for create API
      const validEntries = validatedEntries
        .filter((entry) => entry.isValid)
        .map((entry) => {
          const payout = {
            userId: entry.userId,
            email: entry.email,
            phone: entry.phone,
            host: entry.host,
            transactionType: entry.transactionType,
            amount: entry.amount,
            periodStartDate: entry.periodStartDate,
            periodEndDate: entry.periodEndDate,
            remark: entry.remark,
          };

          // Only send releaseDate when present (optional for credit entries)
          if (entry.releaseDate) {
            payout.releaseDate = entry.releaseDate;
          }

          return payout;
        });

      const response = await createBulkPayouts({ entries: validEntries });

      if (response?.code === 201 || response?.success) {
        setStep("success");
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(response?.message || "Failed to create payouts");
      }
    } catch (err) {
      setError(err.message || "Error creating payouts");
      setStep("results");
    } finally {
      setLoading(false);
    }
  };

  const allEntriesValid = validatedEntries.every((entry) => entry.isValid);
  const validCount = validatedEntries.filter((entry) => entry.isValid).length;
  const invalidCount = validatedEntries.filter(
    (entry) => !entry.isValid
  ).length;

  const columns = [
    {
      title: "Status",
      key: "status",
      width: 100,
      fixed: "left",
      render: (_, record) => {
        const tag = (
          <Tag
            icon={
              record.isValid ? <CheckCircleOutlined /> : <CloseCircleOutlined />
            }
            color={record.isValid ? "success" : "error"}
            style={{ cursor: record.isValid ? "default" : "pointer" }}
          >
            {record.isValid ? "Valid" : "Invalid"}
          </Tag>
        );

        // Show tooltip with error message only for invalid entries
        if (!record.isValid && record.errorMessage) {
          return (
            <Tooltip title={record.errorMessage} color="red">
              {tag}
            </Tooltip>
          );
        }

        return tag;
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 120,
    },
    {
      title: "Host",
      dataIndex: "host",
      key: "host",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "transactionType",
      key: "transactionType",
      width: 80,
      render: (type) => (
        <Tag color={type === "credit" ? "green" : "red"}>
          {type?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 100,
      render: (amount) => (
        <Text>
          <span style={{ fontFamily: "monospace" }}>₹</span>
          {amount?.toLocaleString("en-IN")}
        </Text>
      ),
    },
    {
      title: "Period Start",
      dataIndex: "periodStartDate",
      key: "periodStartDate",
      width: 110,
      render: (date) => formatDateForDisplay(date),
    },
    {
      title: "Period End",
      dataIndex: "periodEndDate",
      key: "periodEndDate",
      width: 110,
      render: (date) => formatDateForDisplay(date),
    },
    {
      title: "Release Date",
      dataIndex: "releaseDate",
      key: "releaseDate",
      width: 110,
      render: (date) => formatDateForDisplay(date),
    },
    {
      title: "Error",
      dataIndex: "errorMessage",
      key: "errorMessage",
      width: 250,
      ellipsis: true,
      render: (msg) => (msg ? <Text type="danger">{msg}</Text> : "-"),
    },
  ];

  const renderUploadStep = () => (
    <div style={{ padding: "20px 0" }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Title level={5} style={{ marginBottom: 8 }}>
            Upload CSV File
          </Title>
          <Text type="secondary">
            Upload a CSV file with payout entries. Required columns:{" "}
            <Text strong>
              email/phone (at least one), host, transactionType, amount,
              periodStartDate, periodEndDate
            </Text>
            . <Text strong>releaseDate</Text> is required for debit entries and
            optional for credit entries.
          </Text>
        </div>

        <Button
          type="link"
          icon={<DownloadOutlined />}
          onClick={handleDownloadSample}
          style={{ padding: 0 }}
        >
          Download Sample CSV
        </Button>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}

        <Dragger
          name="file"
          accept=".csv"
          multiple={false}
          beforeUpload={handleFileUpload}
          showUploadList={false}
          disabled={loading}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag CSV file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Only CSV files are supported. Use DD-MM-YYYY format for dates. The
            file will be validated before payouts are created.
          </p>
        </Dragger>

        <Alert
          message="Important Notes"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>
                <Text strong>email/phone:</Text> At least one is required.
                Recommended to provide both for accurate identification.
              </li>
              <li>
                <Text strong>transactionType:</Text> Must be either{" "}
                <Text code>credit</Text> or <Text code>debit</Text>
              </li>
              <li>
                <Text strong>Dates:</Text> Use format DD-MM-YYYY (e.g.,
                01-01-2025)
              </li>
              <li>
                <Text strong>releaseDate:</Text> Required for{" "}
                <Text code>debit</Text> entries. Optional for{" "}
                <Text code>credit</Text> entries (can be left empty).
              </li>
              <li>
                <Text strong>amount:</Text> Must be a positive number
              </li>
              <li>
                <Text strong>remark:</Text> Optional field for notes
              </li>
            </ul>
          }
          type="info"
          showIcon
        />
      </Space>
    </div>
  );

  const renderValidatingStep = () => (
    <div
      style={{
        padding: "60px 0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Spin size="large" />
      <Text style={{ marginTop: 16 }}>Validating entries...</Text>
    </div>
  );

  const renderResultsStep = () => (
    <div style={{ padding: "10px 0" }}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Space>
            <Tag color="success" style={{ fontSize: 14, padding: "4px 12px" }}>
              Valid: {validCount}
            </Tag>
            <Tag color="error" style={{ fontSize: 14, padding: "4px 12px" }}>
              Invalid: {invalidCount}
            </Tag>
          </Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadValidationResults}
          >
            Download Results CSV
          </Button>
        </div>

        {!allEntriesValid && (
          <Alert
            message="Some entries have errors"
            description="Please review and fix the errors in your CSV file. Payouts cannot be created until all entries are valid."
            type="error"
            showIcon
          />
        )}

        {allEntriesValid && (
          <Alert
            message="All entries are valid!"
            description="You can now proceed to create the payouts."
            type="success"
            showIcon
          />
        )}

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}

        <Table
          columns={columns}
          dataSource={validatedEntries.map((entry, index) => ({
            ...entry,
            key: index,
          }))}
          size="small"
          scroll={{ x: 1400 }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} entries`,
          }}
        />
      </Space>
    </div>
  );

  const renderCreatingStep = () => (
    <div
      style={{
        padding: "60px 0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Spin size="large" />
      <Text style={{ marginTop: 16 }}>Creating payouts...</Text>
      <Text type="secondary" style={{ marginTop: 8 }}>
        Please do not close this window
      </Text>
    </div>
  );

  const renderSuccessStep = () => (
    <Result
      status="success"
      title="Payouts Created Successfully!"
      subTitle={`${validCount} payout entries have been created successfully.`}
      extra={[
        <Button key="close" type="primary" onClick={handleClose}>
          Close
        </Button>,
      ]}
    />
  );

  const renderContent = () => {
    switch (step) {
      case "upload":
        return renderUploadStep();
      case "validating":
        return renderValidatingStep();
      case "results":
        return renderResultsStep();
      case "creating":
        return renderCreatingStep();
      case "success":
        return renderSuccessStep();
      default:
        return renderUploadStep();
    }
  };

  const renderFooter = () => {
    if (step === "upload") {
      return [
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
      ];
    }

    if (step === "results") {
      return [
        <Button key="back" onClick={() => setStep("upload")}>
          Upload New File
        </Button>,
        <Button
          key="submit"
          type="primary"
          disabled={!allEntriesValid || loading}
          onClick={handleCreatePayouts}
          loading={loading}
        >
          Add Payouts ({validCount})
        </Button>,
      ];
    }

    if (step === "success") {
      return null; // Footer handled in success result
    }

    return null;
  };

  return (
    <Modal
      title="Bulk Payout Management"
      open={visible}
      onCancel={handleClose}
      width={step === "results" ? 1200 : 700}
      footer={renderFooter()}
      destroyOnClose
      maskClosable={step === "upload"}
    >
      {renderContent()}
    </Modal>
  );
};

BulkPayoutModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

BulkPayoutModal.defaultProps = {
  onSuccess: () => {},
};

export default BulkPayoutModal;
